// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import React, { useState } from 'react';
import clsx from 'clsx';
import { TooltipHost } from 'office-ui-fabric-react';
import { useTranslation } from 'react-i18next';
import ReactHtmlParser from 'react-html-parser';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import tooltipIcon from './../../assets/tooltip-copy-3@2x.png';
import Collapsible from 'src/components/Collapsible';
import Styles from './offer.module.scss';
import { capitalize } from 'lodash';

interface HeaderText {
    description: string;
    title: string;
}

interface PropTypes {
    columnHeaders: any[];
    items: any[];
    readonly: boolean;
    selectedDetailsKey: any[];
    collapsibleHeader: HeaderText;
    collapsibleBody: HeaderText[];
    noRecordMessageKey: string;
    onChange: (items: any[]) => void;
    tableName?: string;
    offerKey: number;
}

export default function Offer(props: PropTypes): JSX.Element {
    const { t } = useTranslation();
    const [{ }, setSelectedItems] = useState([] as any[]);
    const onSelectionChange = (e: any) => {
        const { value } = e.target;
        const selectedItems: any[] = [];
        props.items.map((item: any) => {
            if (item.option.offerId === value) {
                selectedItems.push(item);
                setSelectedItems(selectedItems);
                props.onChange(selectedItems);
            }
        });
    };

    const renderOptions = (item?: any) => {
        const option = item && item.option;
        return option ? (
            <div>
                <span className={Styles.itemTitle}>
                    {item.option.title}
                    {item.option.toolTip && (
                        <TooltipHost
                            calloutProps={{ gapSpace: 0 }}
                            content={item.option.toolTip}
                            id="item_option"
                        >
                            <img
                                alt={item.option.toolTip}
                                aria-label={item.option.toolTip}
                                className={Styles.toolTip}
                                src={tooltipIcon}
                                tabIndex={0}
                                aria-describedby="item_option"
                            />
                        </TooltipHost>
                    )}
                </span>
                <p className={clsx(Styles.itemDescription, Styles.itemMargin)}>
                    {item.option.description}
                </p>
            </div>
        ) : null;
    };

    const renderCost = (item?: any) => {
        const cost = item && item.cost;
        return cost ? (
            <div>
                <span>
                    {item.cost}
                </span>
            </div>
        ) : null;
    };

    const WarningIcon = () => <Icon className={clsx('ms-Icon', Styles.warningIcon)} iconName="warning" ariaLabel="warning" />;
    const offerHeader = (header: HeaderText) => {
        return (
            <section className={clsx(Styles.container, 'ms-Grid-row')}>
                <div className={clsx('ms-Grid-row', Styles.paddingVertical8)}>
                    <h3 className={clsx('ms-Grid-col', 'ms-sm4')}> {header.title}</h3>
                    <div className={clsx('ms-Grid-col', 'ms-sm8', Styles.collapsibleHeaderDesc)} >{header.description}</div>
                </div>
            </section>
        );
    };

    const { collapsibleBody } = props;
    const offerAvailable = !!props.items.length;

    const warningMessages: HeaderText[] = collapsibleBody.map(
        (message: any) => ({
            title: message.title,
            description: message.description,
        })
    );

    if (!offerAvailable) {
        warningMessages.push({
            title: t('no_offer_found'),
            description: '',
        });
    }

    return (
        <Collapsible
            colorOnOpen={'white'}
            colorOnClose={'white'}
            chevronColor={'black'}
            isOpen={true}
            header={offerHeader(props.collapsibleHeader)}
            className={Styles.offer}
            tableName={props.tableName}
        >
            <>
                {Array.isArray(warningMessages) && warningMessages.map((warningMessage: HeaderText, key: number) => (
                    <section className={clsx(Styles.container, 'ms-Grid-row', Styles.border)} key={key}>
                        <div className={clsx('ms-Grid-row')}>
                            <h3 className={clsx('ms-Grid-col', 'ms-sm12', Styles.paddingTop1, Styles.itemTitle)}>
                                <WarningIcon /> <span>{warningMessage.title}</span>
                            </h3>
                        </div>
                        <div className={clsx('ms-Grid-row')}>
                            <div className={clsx('ms-Grid-col', 'ms-sm112', Styles.itemDescription, Styles.description)}>{ReactHtmlParser(warningMessage.description)}</div>
                        </div>
                    </section>
                ))
                }
                {offerAvailable ?
                    (<div className={clsx('ms-Grid', Styles.grid)}>
                        <div className="ms-Grid-row">
                            <div className={clsx('ms-sm12', Styles.gridParent)}>
                                <table className={Styles.tableNonBordered} role="grid">
                                    <thead>
                                        <tr role="row">
                                            {React.Children.toArray(
                                                props.columnHeaders.map((column: any) => (
                                                    <>
                                                        <th role="gridcell">
                                                            <div style={{ width: column.maxWidth }}>{capitalize(column.name)}</div>
                                                        </th>
                                                        <th style={{ width: 20 }} />
                                                    </>
                                                ))
                                            )}
                                            <th role="gridcell">
                                                <div style={{ width: 40 }}>{t('select')}</div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr role="row" style={props.items.length > 0 ? { display: 'none' } : { textDecorationColor: 'black' }}>
                                            <td role="gridcell" className={Styles.tdNoRecordFound}>
                                                {t('no_records_found')}
                                            </td>
                                        </tr>
                                        {React.Children.toArray(
                                            props.items.map((item: any) => (
                                                <tr role="row">
                                                    {React.Children.toArray(
                                                        props.columnHeaders.map((column: any) => {
                                                            if (column.fieldName === 'option') {
                                                                return (<>
                                                                    <td role="gridcell" style={{ width: column.maxWidth }}>
                                                                        {renderOptions(item)}
                                                                    </td>
                                                                    <td style={{ width: 20 }} />
                                                                </>);
                                                            }
                                                            return (<>
                                                                <td role="gridcell" style={{ width: column.maxWidth }}>
                                                                    {renderCost(item)}
                                                                </td>
                                                                <td style={{ width: 20 }} />
                                                            </>);
                                                        })
                                                    )}
                                                    <td role="gridcell" style={{ width: 40 }}>
                                                        <input type="radio" style={{ width: 20, height: 20, verticalAlign: 'middle' }}
                                                            id={t('select') + props.offerKey} name={t('select') + props.offerKey}
                                                            disabled={props.readonly}
                                                            value={item.option.offerId}
                                                            onChange={onSelectionChange} />
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>)
                    : null}
                {/* {document.querySelectorAll('div.ms-List-cell div.ms-DetailsRow-cell span[aria-label=\'Cost\:\ \']').forEach((role)=>{
                    role.setAttribute('role', 'term');
                })} */}
            </>
        </Collapsible >
    );
}
